import { useState, useEffect } from 'react';
import { CheckCircle, Star, Shield, TrendingUp, Users, Gift, Eye, EyeOff, Mail, Lock, User, AlertCircle, Phone, Check } from 'lucide-react';
import { useTheme } from '@/react-app/contexts/ThemeContext';
import { useAuth } from '@getmocha/users-service/react';
import { useAuthStore } from '@/shared/stores/authStore';
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
// import GoogleLoginButton from "../components/GoogleLogin";
import tradeLogo from "../../assets/trade-logo.jpeg";



export default function Register() {
    const { isPending } = useAuth();
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const [currentStep, setCurrentStep] = useState(0);
    const [authMethod, setAuthMethod] = useState<'google' | 'email'>('google');
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [error, setError] = useState('');
    const [passwordStrength, setPasswordStrength] = useState(0);
    const navigate = useNavigate();
    const { isAuthenticated } = useAuthStore();

    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        confirmPassword: '',
        phoneNumber: '',
        gender: '',
        acceptTerms: false,
    });

    const {
        register: authStoreRegister,
        isLoading: authStoreLoading,
        error: authStoreError,
        clearError: authStoreClearError,
    } = useAuthStore();


    const handleGetStarted = (ride: string) => {
        if (ride == "learn") {
            navigate('/about');
        } else {

            if (isAuthenticated) {
                navigate('/save');
            } else {
                navigate('/login');
            }
        }
    };

    const handleEmailSignUp = async (e: React.FormEvent<HTMLFormElement>) => {

        try {
            // Prevent default form submission immediately
            e.preventDefault();
            e.stopPropagation();

            // Prevent if already loading
            if (authStoreLoading) {
                return false;
            }

            authStoreClearError(); // Clear any previous errors from the store

            // setIsLoading(true);
            setError('');

            if (formData.password !== formData.confirmPassword) {
                setError('Passwords do not match');
                toast.error("Passwords do not match");
                return false;
            }

            if (!formData.acceptTerms) {
                setError('Please accept the terms and conditions');
                toast.error("Please accept the terms and conditions");
                return false;
            }

            if (!formData.firstName.trim()) {
                setError('First name is required');
                toast.error("First name is required");
                return false;
            }

            if (!formData.lastName.trim()) {
                setError('Last name is required');
                toast.error("Last name is required");
                return false;
            }

            if (!formData.phoneNumber.trim()) {
                setError('Phone number is required');
                toast.error("Phone number is required");
                return false;
            }

            if (!formData.gender) {
                setError('Please select your gender');
                toast.error("Please select your gender");
                return false;
            }

            const passwordRequirements = validatePassword(formData.password);
            const isPasswordValid = Object.values(passwordRequirements).every(Boolean);

            if (!isPasswordValid) {
                setError('password does not meet all requirements');
                toast.error("password does not meet all requirements'");
                return;
            }

            const searchParams = new URLSearchParams(window.location.search);
            const referrerCode = searchParams.get('ref');


            const success = await authStoreRegister({
                firstName: formData.firstName,
                lastName: formData.lastName,
                email: formData.email,
                password: formData.password,
                phone: formData.phoneNumber,
                gender: formData.gender,
                referralCode: referrerCode || "",
                role: "user"
            });


            if (success) {
                toast.success("Account created successfully 🎉");
                setTimeout(() => navigate("/dashboard"), 1500);
            } else {
                toast.error(authStoreError || "Failed to create account");
            }
            return false;
        } catch (error: any) {
            console.error('Registration error:', error);
            toast.error(authStoreError);
            return false;
        }
    };

    const validatePassword = (password: string) => {
        const requirements = {
            length: password.length >= 8,
            uppercase: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            number: /\d/.test(password),
            special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
        };

        return requirements;
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { name, value, type } = e.target;
        const checked = 'checked' in e.target ? e.target.checked : false;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));

        // Calculate password strength
        if (name === 'password') {
            let strength = 0;
            if (value.length >= 8) strength++;
            if (/[a-z]/.test(value)) strength++;
            if (/[A-Z]/.test(value)) strength++;
            if (/[0-9]/.test(value)) strength++;
            if (/[^A-Za-z0-9]/.test(value)) strength++;
            setPasswordStrength(strength);
        }
    };

    const onboardingSteps = [
        {
            icon: CheckCircle,
            title: 'Create Your Account',
            description: 'Sign up with Google OAuth or email in seconds',
            color: 'blue'
        },
        {
            icon: TrendingUp,
            title: 'Choose Your Plan',
            description: 'Select a savings package that fits your goals',
            color: 'green'
        },
        {
            icon: Shield,
            title: 'Make Your First Deposit',
            description: 'Start with as little as $500',
            color: 'purple'
        },
        {
            icon: Star,
            title: 'Start Earning',
            description: 'Watch your money grow monthly',
            color: 'orange'
        }
    ];

    const benefits = [
        {
            icon: TrendingUp,
            title: 'Consistent Returns',
            description: 'Earn up to 10% monthly returns on your savings',
            highlight: 'Up to 10%'
        },
        {
            icon: Shield,
            title: 'Secure Platform',
            description: 'Bank-level encryption and security measures',
            highlight: 'Bank-Level Security'
        },
        {
            icon: Users,
            title: 'Growing Community',
            description: 'Join 5,000+ smart savers building wealth',
            highlight: '5,000+ savers'
        },
        {
            icon: Gift,
            title: 'Referral Bonuses',
            description: 'Earn extra when you refer friends',
            highlight: 'Bonus Rewards'
        }
    ];

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentStep((prev) => (prev + 1) % onboardingSteps.length);
        }, 3000);
        return () => clearInterval(interval);
    }, []);

    const getPasswordStrengthColor = () => {
        if (passwordStrength <= 2) return 'bg-red-500';
        if (passwordStrength <= 3) return 'bg-yellow-500';
        return 'bg-green-500';
    };

    const getPasswordStrengthText = () => {
        if (passwordStrength <= 2) return 'Weak';
        if (passwordStrength <= 3) return 'Medium';
        return 'Strong';
    };

    if (isPending) {
        return (
            <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 via-white to-purple-50'} flex items-center justify-center`}>
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className={`min-h-screen ${isDark ? 'bg-gray-900' : 'bg-gradient-to-br from-blue-50 via-white to-purple-50'}`}>
            {/* Header */}
            <header className={`${isDark ? 'bg-gray-800/80' : 'bg-white/80'} backdrop-blur-md border-b ${isDark ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex justify-between items-center h-16">
                        <a href="/" className="flex items-center space-x-2">
                            <img src={tradeLogo} alt="logo" className="h-10" />
                            <span className={`text-xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Trade Enrich</span>
                        </a>
                        <a
                            href="/login"
                            className={`${isDark ? 'text-gray-300 hover:text-blue-400' : 'text-gray-700 hover:text-blue-600'} px-4 py-2 rounded-md text-sm font-medium transition-colors`}
                        >
                            Already have an account? Sign in
                        </a>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">

                    {/* Left Side - Hero Content */}
                    <div className="space-y-8">
                        <div>
                            <h1 className={`text-4xl lg:text-6xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} leading-tight`}>
                                Start Building
                                <span className="gradient-text block">Wealth Today</span>
                            </h1>
                            <p className={`text-xl ${isDark ? 'text-gray-300' : 'text-gray-600'} mt-6 leading-relaxed`}>
                                Join thousands of smart savers who are earning consistent monthly returns
                                while building long-term financial security. Start your wealth-building journey
                                with Trade Enrich today.
                            </p>
                        </div>

                        {/* Animated Onboarding Steps */}
                        <div className={`${isDark ? 'bg-gray-800/70' : 'bg-white/70'} backdrop-blur-sm rounded-2xl p-6 border ${isDark ? 'border-gray-700/30' : 'border-white/30'}`}>
                            <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'} mb-4`}>How it works:</h3>
                            <div className="space-y-3">
                                {onboardingSteps.map((step, index) => (
                                    <div
                                        key={index}
                                        className={`flex items-center space-x-3 p-3 rounded-xl transition-all duration-500 ${currentStep === index
                                            ? `${isDark ? 'bg-gray-700' : 'bg-blue-50'} border-2 ${isDark ? 'border-blue-500' : 'border-blue-200'} transform scale-105`
                                            : isDark ? 'bg-gray-800/50' : 'bg-white/50'
                                            }`}
                                    >
                                        <div className={`p-2 rounded-full bg-${step.color}-100`}>
                                            <step.icon className={`h-4 w-4 text-${step.color}-600`} />
                                        </div>
                                        <div>
                                            <div className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{step.title}</div>
                                            <div className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{step.description}</div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Stats */}
                        <div className="grid grid-cols-3 gap-6">
                            <div className="text-center">
                                <div className="text-3xl font-bold text-blue-600">$2M+</div>
                                <div className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Total saveed</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-bold text-green-600">5K+</div>
                                <div className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Happy savers</div>
                            </div>
                            <div className="text-center">
                                <div className="text-3xl font-bold text-purple-600">98%</div>
                                <div className={`${isDark ? 'text-gray-400' : 'text-gray-600'} text-sm`}>Satisfaction</div>
                            </div>
                        </div>
                    </div>

                    {/* Right Side - Registration Form */}
                    <div className={`${isDark ? 'bg-gray-800' : 'bg-white'} rounded-3xl shadow-2xl p-8 lg:p-12`}>
                        <div className="text-center mb-8">
                            <div className="bg-blue-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                                <Star className="h-8 w-8 text-blue-600" />
                            </div>
                            <h2 className={`text-3xl font-bold ${isDark ? 'text-white' : 'text-gray-900'} mb-2`}>Create Your Account</h2>
                            <p className={isDark ? 'text-gray-300' : 'text-gray-600'}>Start your wealth building journey in seconds</p>
                        </div>

                        {/* Auth Method Toggle */}
                        <div className={`flex rounded-xl p-1 mb-6 ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
                            <button
                                onClick={() => setAuthMethod('google')}
                                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${authMethod === 'google'
                                    ? 'bg-blue-600 text-white shadow-md'
                                    : isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                Google OAuth
                            </button>
                            <button
                                onClick={() => setAuthMethod('email')}
                                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all duration-200 ${authMethod === 'email'
                                    ? 'bg-blue-600 text-white shadow-md'
                                    : isDark ? 'text-gray-300 hover:text-white' : 'text-gray-600 hover:text-gray-900'
                                    }`}
                            >
                                Email & Password
                            </button>
                        </div>

                        {/* Google Sign Up */}
                        {authMethod === 'google' && (
                            <>
                                {/* <GoogleLoginButton /> */}

                                <p>Coming soon</p>
                                <div className="relative mb-6">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className={`w-full border-t ${isDark ? 'border-gray-600' : 'border-gray-300'}`}></div>
                                    </div>
                                    <div className="relative flex justify-center text-sm">
                                        <span className={`px-4 ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-white text-gray-500'}`}>Fast & Secure Signup</span>
                                    </div>
                                </div>
                            </>
                        )}

                        {/* Email/Password Sign Up */}
                        {authMethod === 'email' && (
                            <>
                                <form className="space-y-6 mb-6" onSubmit={handleEmailSignUp}>
                                    {(authStoreError || error) && (
                                        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center space-x-2">
                                            <AlertCircle className="h-5 w-5 text-red-500" />
                                            <span className="text-red-700 text-sm">{authStoreError || error}</span>
                                        </div>
                                    )}

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                        <div>
                                            <label htmlFor="firstName" className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                                First Name
                                            </label>
                                            <div className="relative">
                                                <User className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                                                <input
                                                    type="text"
                                                    id="firstName"
                                                    name="firstName"
                                                    required
                                                    value={formData.firstName}
                                                    onChange={handleInputChange}
                                                    className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${isDark
                                                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                                        }`}
                                                    placeholder="First name"
                                                />
                                            </div>
                                        </div>

                                        <div>
                                            <label htmlFor="lastName" className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                                Last Name
                                            </label>
                                            <div className="relative">
                                                <User className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                                                <input
                                                    type="text"
                                                    id="lastName"
                                                    name="lastName"
                                                    required
                                                    value={formData.lastName}
                                                    onChange={handleInputChange}
                                                    className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${isDark
                                                        ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                                        : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                                        }`}
                                                    placeholder="Last name"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="email" className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                            Email Address
                                        </label>
                                        <div className="relative">
                                            <Mail className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                                            <input
                                                type="email"
                                                id="email"
                                                name="email"
                                                required
                                                value={formData.email}
                                                onChange={handleInputChange}
                                                className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${isDark
                                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                                    }`}
                                                placeholder="Enter your email"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="password" className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                            Password
                                        </label>
                                        <div className="relative">
                                            <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                                            <input
                                                type={showPassword ? 'text' : 'password'}
                                                id="password"
                                                name="password"
                                                required
                                                value={formData.password}
                                                onChange={handleInputChange}
                                                className={`w-full pl-10 pr-12 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${isDark
                                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                                    }`}
                                                placeholder="Create a strong password"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowPassword(!showPassword)}
                                                className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                                            >
                                                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                            </button>
                                        </div>
                                        {formData.password && (
                                            <div className="mt-2">
                                                <div className="flex items-center justify-between mb-1">
                                                    <span className={`text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Password strength:</span>
                                                    <span className={`text-xs font-medium ${passwordStrength <= 2 ? 'text-red-500' : passwordStrength <= 3 ? 'text-yellow-500' : 'text-green-500'}`}>
                                                        {getPasswordStrengthText()}
                                                    </span>
                                                </div>
                                                <div className={`w-full bg-gray-200 rounded-full h-2 ${isDark ? 'bg-gray-600' : 'bg-gray-200'}`}>
                                                    <div
                                                        className={`h-2 rounded-full transition-all duration-300 ${getPasswordStrengthColor()}`}
                                                        style={{ width: `${(passwordStrength / 5) * 100}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <div>
                                        <label htmlFor="confirmPassword" className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                            Confirm Password
                                        </label>
                                        <div className="relative">
                                            <Lock className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                                            <input
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                id="confirmPassword"
                                                name="confirmPassword"
                                                required
                                                value={formData.confirmPassword}
                                                onChange={handleInputChange}
                                                className={`w-full pl-10 pr-12 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${isDark
                                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                                    }`}
                                                placeholder="Confirm your password"
                                            />
                                            <button
                                                type="button"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                className={`absolute right-3 top-1/2 transform -translate-y-1/2 ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}
                                            >
                                                {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                                            </button>
                                        </div>
                                    </div>
                                    {formData.confirmPassword && formData.password !== formData.confirmPassword && (
                                        <p className="mt-2 text-sm text-red-600 dark:text-red-400">
                                            Passwords do not match
                                        </p>
                                    )}

                                    <div>
                                        {formData.password && (
                                            <div className={`mt-3 p-4 ${isDark ? 'bg-gray-600' : 'bg-white'} rounded-lg`}>
                                                <p className={`text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                                    Password Requirements:
                                                </p>
                                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                                    {[
                                                        { key: 'length', text: 'At least 8 characters' },
                                                        { key: 'uppercase', text: 'One uppercase letter' },
                                                        { key: 'lowercase', text: 'One lowercase letter' },
                                                        { key: 'number', text: 'One number' },
                                                        { key: 'special', text: 'One special character' }
                                                    ].map(({ key, text }) => {
                                                        const requirements = validatePassword(formData.password);
                                                        const isValid = requirements[key as keyof typeof requirements];
                                                        return (
                                                            <div key={key} className="flex items-center space-x-2">
                                                                <div className={`w-4 h-4 rounded-full flex items-center justify-center ${isValid ? 'bg-green-500' : isDark ? 'bg-gray-500' : 'bg-gray-300'
                                                                    }`}>
                                                                    {isValid && <Check className="h-3 w-3 text-white" />}
                                                                </div>
                                                                <span className={`text-sm ${isValid
                                                                    ? 'text-green-600 dark:text-green-400'
                                                                    : isDark ? 'text-gray-400' : 'text-gray-600'
                                                                    }`}>
                                                                    {text}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label htmlFor="phoneNumber" className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                            Phone Number
                                        </label>
                                        <div className="relative">
                                            <Phone className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 ${isDark ? 'text-gray-400' : 'text-gray-400'}`} />
                                            <input
                                                type="tel"
                                                id="phoneNumber"
                                                name="phoneNumber"
                                                required
                                                value={formData.phoneNumber}
                                                onChange={handleInputChange}
                                                className={`w-full pl-10 pr-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${isDark
                                                    ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
                                                    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
                                                    }`}
                                                placeholder="Enter your phone number"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="gender" className={`block text-sm font-medium ${isDark ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                                            Gender
                                        </label>
                                        <select
                                            id="gender"
                                            name="gender"
                                            required
                                            value={formData.gender}
                                            onChange={handleInputChange}
                                            className={`w-full px-4 py-3 border rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors ${isDark
                                                ? 'bg-gray-700 border-gray-600 text-white'
                                                : 'bg-white border-gray-300 text-gray-900'
                                                }`}
                                        >
                                            <option value="">Select your gender</option>
                                            <option value="male">Male</option>
                                            <option value="female">Female</option>
                                        </select>
                                    </div>

                                    <div className="flex items-start">
                                        <input
                                            type="checkbox"
                                            id="acceptTerms"
                                            name="acceptTerms"
                                            required
                                            checked={formData.acceptTerms}
                                            onChange={handleInputChange}
                                            className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                        />
                                        <label htmlFor="acceptTerms" className={`ml-3 text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                                            I agree to the{' '}
                                            <a href="/terms" className="text-blue-600 hover:underline">Terms of Service</a>{' '}
                                            and{' '}
                                            <a href="/privacy" className="text-blue-600 hover:underline">Privacy Policy</a>
                                        </label>
                                    </div>

                                    <button
                                        type="submit"
                                        disabled={authStoreLoading || !formData.acceptTerms || !formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim() || !formData.password.trim() || !formData.confirmPassword.trim() || !formData.phoneNumber.trim() || !formData.gender}
                                        onClick={(e) => {
                                            e.preventDefault();
                                            handleEmailSignUp(e as any);
                                        }}
                                        className="w-full bg-blue-600 text-white px-6 py-4 rounded-xl font-semibold hover:bg-blue-700 transition-all duration-300 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {authStoreLoading ? 'Creating Account...' : 'Create Account'}
                                    </button>
                                </form>

                                <div className="relative mb-6">
                                    <div className="absolute inset-0 flex items-center">
                                        <div className={`w-full border-t ${isDark ? 'border-gray-600' : 'border-gray-300'}`}></div>
                                    </div>
                                    <div className="relative flex justify-center text-sm">
                                        <span className={`px-4 ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-white text-gray-500'}`}>Or continue with</span>
                                    </div>
                                </div>

                                {/* <GoogleLoginButton /> */}
                            </>
                        )}

                        {/* Benefits */}
                        {authMethod === 'google' && (
                            <div className="space-y-4 mb-6">
                                {benefits.map((benefit, index) => (
                                    <div key={index} className={`flex items-center space-x-3 p-3 ${isDark ? 'bg-gray-700' : 'bg-gray-50'} rounded-lg`}>
                                        <div className={`p-1.5 ${isDark ? 'bg-gray-600' : 'bg-white'} rounded-lg`}>
                                            <benefit.icon className="h-4 w-4 text-blue-600" />
                                        </div>
                                        <div className="flex-1">
                                            <div className={`text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{benefit.title}</div>
                                            <div className={`text-xs ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{benefit.description}</div>
                                        </div>
                                        <div className="text-xs font-semibold text-blue-600 bg-blue-100 px-2 py-1 rounded">
                                            {benefit.highlight}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {/* Trust Indicators */}
                        <div className={`${isDark ? 'bg-gray-700' : 'bg-green-50'} rounded-xl p-4 mb-6`}>
                            <h4 className={`font-semibold ${isDark ? 'text-white' : 'text-green-900'} mb-2 flex items-center`}>
                                <Shield className="h-4 w-4 mr-2" />
                                Why Trade Enrich?
                            </h4>
                            <ul className={`text-sm ${isDark ? 'text-gray-300' : 'text-green-800'} space-y-1`}>
                                <li>• SEC compliant and fully regulated</li>
                                <li>• 256-bit SSL encryption</li>
                                <li>• Transparent fee structure</li>
                                <li>• 24/7 customer support</li>
                            </ul>
                        </div>

                        {/* Footer Links */}
                        <div className="text-center space-y-4">
                            <div className="flex justify-center space-x-6 text-sm">
                                <a href="/about" className={`${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'} transition-colors`}>
                                    About Us
                                </a>
                                <a href="/contact" className={`${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'} transition-colors`}>
                                    Help Center
                                </a>
                                <a href="/" className={`${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-700'} transition-colors`}>
                                    Back to Home
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom CTA Section */}
            <section className={`py-16 ${isDark ? 'bg-gradient-to-r from-gray-800 to-gray-700' : 'bg-gradient-to-r from-blue-600 to-purple-600'}`}>
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
                    <h2 className="text-3xl font-bold mb-4">Ready to Start Earning?</h2>
                    <p className={`text-xl ${isDark ? 'text-gray-300' : 'text-blue-100'} mb-8`}>
                        Join our community of smart savers and start building wealth today.
                    </p>
                    <button
                        onClick={() => handleGetStarted("log")}
                        className={`${isDark ? 'bg-blue-600 hover:bg-blue-700' : 'bg-white text-blue-600 hover:bg-blue-50'} px-8 py-4 rounded-xl text-lg font-semibold transition-all duration-300 shadow-2xl hover:shadow-3xl`}
                    >
                        Get Started Now
                    </button>
                </div>
            </section>
        </div>
    );
}
